import React, { useState } from 'react';

export type CollapsibleSectionData = {
  label?: string;
  expanded?: boolean;
  onToggle?: () => void;
  expandable?: boolean;
  id?: string;
};

type Props = Readonly<{
  data: CollapsibleSectionData;
  children?: React.ReactNode;
}>;

function isExpandable(data: CollapsibleSectionData) {
  return data.expandable !== false;
}

function isControlled(data: CollapsibleSectionData) {
  return data.expanded !== undefined;
}

function shouldShowPanel(
  children: React.ReactNode,
  expandable: boolean,
  expanded: boolean
) {
  return Boolean(children) && (!expandable || expanded);
}

function ToggleButton({
  id,
  label,
  expanded,
  onToggle,
}: {
  id?: string;
  label: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      id={id || undefined}
      className="collapsible-section__toggle"
      aria-expanded={expanded}
      onClick={onToggle}
    >
      <span className="collapsible-section__label">{label}</span>
      <span
        className={expanded ? 'icon icon-arrow-up' : 'icon icon-arrow-down'}
        aria-hidden="true"
      />
    </button>
  );
}

function StaticHeader({ id, label }: { id?: string; label: string }) {
  return (
    <div className="collapsible-section__toggle" id={id || undefined}>
      <span className="collapsible-section__label">{label}</span>
    </div>
  );
}

export function CollapsibleSection({ data, children }: Props) {
  const [uncontrolledExpanded, setUncontrolledExpanded] = useState(false);

  if (!data?.label) {
    return null;
  }

  const expandable = isExpandable(data);
  const controlled = isControlled(data);
  const expanded = controlled ? Boolean(data.expanded) : uncontrolledExpanded;

  const handleToggle = () => {
    if (!controlled) {
      setUncontrolledExpanded((current) => !current);
    }
    data.onToggle?.();
  };

  const showPanel = shouldShowPanel(children, expandable, expanded);

  return (
    <div className={showPanel ? 'collapsible-section is-expanded' : 'collapsible-section'}>
      {expandable ? (
        <ToggleButton
          id={data.id}
          label={data.label}
          expanded={expanded}
          onToggle={handleToggle}
        />
      ) : (
        <StaticHeader id={data.id} label={data.label} />
      )}
      {showPanel ? <div className="collapsible-section__panel">{children}</div> : null}
    </div>
  );
}
